import type { VideoSelection } from "@meta-video/contracts";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  notifySameDocumentSearchChange,
  subscribeToLocationSearchChanges,
} from "../../shared/url/locationSearch.js";
import {
  rawSearch,
  removeComparisonView,
} from "../../shared/url/comparisonSearch.js";
import {
  parseVideoWorkspaceSearch,
  removeVideoWorkspaceSearch,
  serializeVideoWorkspaceSearch,
  WorkspaceSearchError,
} from "../../shared/url/workspaceSearch.js";

const HISTORY_STATE_KEY = "__metaVideoDetail";
const OPEN_HISTORY_STATE = "open";
const CLOSED_HISTORY_STATE = "closed";

type DetailHistoryState = Record<string, unknown> & {
  readonly [HISTORY_STATE_KEY]?: typeof OPEN_HISTORY_STATE | typeof CLOSED_HISTORY_STATE;
};

export interface VideoUrlState {
  readonly closeDetail: () => void;
  readonly error: string | null;
  readonly focusVideo: (selection: VideoSelection | null) => void;
  readonly isDetailOpen: boolean;
  readonly openDetail: () => void;
  readonly selection: VideoSelection | null;
}

function stateWithoutDetailMarker(): DetailHistoryState {
  const current = window.history.state;
  if (current === null || typeof current !== "object") return {};
  const next = { ...(current as Record<string, unknown>) };
  delete next[HISTORY_STATE_KEY];
  return next;
}

function pathWithSearch(search: string): string {
  return `${window.location.pathname}${search === "" ? "" : `?${search}`}${window.location.hash}`;
}

export function useVideoUrlState(): VideoUrlState {
  const [search, setSearch] = useState(() => window.location.search);
  const [writeError, setWriteError] = useState<string | null>(null);
  const closingWithBackRef = useRef(false);

  useEffect(() => {
    const onLocationSearchChange = () => {
      closingWithBackRef.current = false;
      setWriteError(null);
      setSearch(window.location.search);
    };
    return subscribeToLocationSearchChanges(onLocationSearchChange);
  }, []);

  const parsed = useMemo(() => parseVideoWorkspaceSearch(search), [search]);
  const selection = parsed.kind === "valid" ? parsed.video : null;
  const error = writeError ?? (parsed.kind === "invalid" ? parsed.message : null);
  const isDetailOpen = error === null
    && parsed.kind === "valid"
    && parsed.videoView
    && parsed.video !== null;

  const replaceSearch = useCallback((nextSearch: string) => {
    closingWithBackRef.current = false;
    window.history.replaceState(
      stateWithoutDetailMarker(),
      "",
      pathWithSearch(nextSearch),
    );
    notifySameDocumentSearchChange();
  }, []);

  const focusVideo = useCallback((nextSelection: VideoSelection | null) => {
    try {
      const nextSearch = serializeVideoWorkspaceSearch(
        { video: nextSelection, videoView: false },
        window.location.search,
      );
      if (nextSearch === rawSearch(window.location.search)) {
        setWriteError(null);
        return;
      }
      replaceSearch(nextSearch);
      setWriteError(null);
    } catch (caught) {
      if (!(caught instanceof WorkspaceSearchError)) throw caught;
      setWriteError("This video identity is invalid or too large to share.");
    }
  }, [replaceSearch]);

  const openDetail = useCallback(() => {
    const current = parseVideoWorkspaceSearch(window.location.search);
    if (
      current.kind !== "valid"
      || current.video === null
      || current.videoView
    ) return;
    try {
      const nextSearch = serializeVideoWorkspaceSearch(
        { video: current.video, videoView: true },
        removeComparisonView(window.location.search),
      );
      window.history.pushState(
        { ...stateWithoutDetailMarker(), [HISTORY_STATE_KEY]: OPEN_HISTORY_STATE },
        "",
        pathWithSearch(nextSearch),
      );
      setWriteError(null);
      notifySameDocumentSearchChange();
    } catch (caught) {
      if (!(caught instanceof WorkspaceSearchError)) throw caught;
      setWriteError("This video identity is invalid or too large to share.");
    }
  }, []);

  const closeDetail = useCallback(() => {
    if (closingWithBackRef.current) return;
    const historyState = window.history.state as DetailHistoryState | null;
    if (historyState?.[HISTORY_STATE_KEY] === OPEN_HISTORY_STATE) {
      closingWithBackRef.current = true;
      window.history.back();
      return;
    }
    const current = parseVideoWorkspaceSearch(window.location.search);
    if (current.kind !== "valid") {
      replaceSearch(removeVideoWorkspaceSearch(window.location.search));
      return;
    }
    replaceSearch(serializeVideoWorkspaceSearch(
      { video: current.video, videoView: false },
      window.location.search,
    ));
  }, [replaceSearch]);

  useEffect(() => {
    if (parsed.kind !== "invalid") return;
    const nextSearch = removeVideoWorkspaceSearch(window.location.search);
    window.history.replaceState(
      stateWithoutDetailMarker(),
      "",
      pathWithSearch(nextSearch),
    );
    notifySameDocumentSearchChange();
    setWriteError("The shared video state was invalid and has been removed.");
  }, [parsed.kind]);

  useLayoutEffect(() => {
    if (!isDetailOpen || parsed.kind !== "valid" || parsed.video === null) return;
    const historyState = window.history.state as DetailHistoryState | null;
    if (historyState?.[HISTORY_STATE_KEY] === OPEN_HISTORY_STATE) return;

    const openSearch = serializeVideoWorkspaceSearch(
      { video: parsed.video, videoView: true },
      removeComparisonView(window.location.search),
    );
    const closedSearch = serializeVideoWorkspaceSearch(
      { video: parsed.video, videoView: false },
      openSearch,
    );
    const baseState = stateWithoutDetailMarker();
    window.history.replaceState(
      { ...baseState, [HISTORY_STATE_KEY]: CLOSED_HISTORY_STATE },
      "",
      pathWithSearch(closedSearch),
    );
    window.history.pushState(
      { ...baseState, [HISTORY_STATE_KEY]: OPEN_HISTORY_STATE },
      "",
      pathWithSearch(openSearch),
    );
    notifySameDocumentSearchChange();
  }, [isDetailOpen, parsed]);

  return {
    closeDetail,
    error,
    focusVideo,
    isDetailOpen,
    openDetail,
    selection,
  };
}
